import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

interface CompanyContextType {
  selectedCompanyId: number | undefined;
  setSelectedCompanyId: (id: number) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >();
  const { data: companies, isLoading } = trpc.company.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Debug logging
  useEffect(() => {
    console.log("[CompanyContext] isLoading:", isLoading);
    console.log("[CompanyContext] companies count:", companies?.length);
    console.log(
      "[CompanyContext] companies data:",
      JSON.stringify(companies, null, 2)
    );
  }, [companies, isLoading]);

  // Auto-select first company if none selected
  useEffect(() => {
    if (!selectedCompanyId && companies && companies.length > 0) {
      const savedCompanyId = localStorage.getItem("selectedCompanyId");
      if (savedCompanyId) {
        const id = parseInt(savedCompanyId);
        const companyExists = companies.find(c => c.id === id);
        if (companyExists) {
          setSelectedCompanyId(id);
          return;
        } else {
          // Clear invalid company ID from localStorage
          console.warn(
            `[CompanyContext] Clearing invalid company ID ${id} from localStorage`
          );
          localStorage.removeItem("selectedCompanyId");
        }
      }
      // Select first available company
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  // Save to localStorage when changed
  const handleSetCompanyId = (id: number) => {
    setSelectedCompanyId(id);
    localStorage.setItem("selectedCompanyId", id.toString());
  };

  return (
    <CompanyContext.Provider
      value={{
        selectedCompanyId,
        setSelectedCompanyId: handleSetCompanyId,
        isLoading: isLoading || !isAuthenticated,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
}
